/* global HTMLElement */
const {keyBy, nextFocusable, outerHeight, find, indexOfList} = require('lib/dom')
const reports = require('lib/reports')
const getRatingForPoints = require('./rating').getRatingForPoints
const translate = require('lib/translate')({
  de: {
    points: '{{count}} Punkt',
    points_plural: '{{count}} Punkte',
    participants: '{{count}} Teilnehmer',
    participants_plural: '{{count}} Teilnehmer',
    oneOfMany: '{{one}} von {{many}}',
    toTheResults: 'Zu den Resultaten',
    nextQuestion: 'Nächste Frage',
    backToArticle: 'Zurück zum Artikel',
    redoQuiz: 'Quiz wiederholen',
    ratingText_0: 'Oh, das war jetzt gar nichts! Sie haben keine Frage richtig beantwortet.',
    ratingText_1: 'Das ginge besser! Sie haben nur wenige Fragen richtig beantwortet.',
    ratingText_2: 'Nicht schlecht. Mal richtig, mal falsch. Wie im echten Leben.',
    ratingText_3: 'Gut! Sie haben die meisten Fragen richtig beantwortet.',
    ratingText_4: 'Fantastisch! Sie haben alle Fragen richtig beantwortet.'
  },
  fr: {
    points: '{{count}} Point',
    points_plural: '{{count}} Points',
    participants: '{{count}} Participant',
    participants_plural: '{{count}} Participants',
    oneOfMany: '{{one}} sur {{many}}',
    toTheResults: 'Aux résultats',
    nextQuestion: 'Question suivante',
    backToArticle: `Retour à l'article`,
    redoQuiz: `Répéter le quiz`,
    ratingText_0: `Oh, ce n'était rien! Vous n'avez pas répondu correctement à une question.`,
    ratingText_1: 'Ce serait mieux! Vous avez répondu correctement à quelques questions seulement.',
    ratingText_2: 'Pas mal. Parfois vrai, parfois faux. Comme dans la vraie vie.',
    ratingText_3: 'Bon! Vous avez répondu correctement à la plupart des questions.',
    ratingText_4: 'Fantastique! Vous avez répondu à toutes les questions correctement.'
  },
  it: {
    points: '{{count}} Punto',
    points_plural: '{{count}} Punti',
    participants: '{{count}} Partecipante',
    participants_plural: '{{count}} Partecipanti',
    oneOfMany: '{{one}} su {{many}}',
    toTheResults: 'Ai risultati',
    nextQuestion: 'Domanda successiva',
    backToArticle: `Torna all'articolo`,
    redoQuiz: `Ripeti il quiz`,
    ratingText_0: 'Oh, non era niente! Non hai risposto correttamente a una domanda.',
    ratingText_1: 'Sarebbe meglio! Hai risposto correttamente solo ad alcune domande.',
    ratingText_2: 'Non male. A volte giusto, a volte sbagliato. Come nella vita reale.',
    ratingText_3: 'Buono! Hai risposto correttamente alla maggior parte delle domande.',
    ratingText_4: 'Fantastico! Hai risposto correttamente a tutte le domande.'
  }
})

const imageServices = {
  imgix: require('../lib/imageServices/imgix')
}

class Tool extends HTMLElement {
  static get observedAttributes () { return ['data-item'] }

  connectedCallback () {
    this.item = JSON.parse(this.getAttribute('data-item'))
    const isQEditor = !!this.ownerDocument.body.getAttribute('aurelia-app')

    const _ns = this.getAttribute('data-ns')
    const namespace = isQEditor ? `${_ns}:draft` : _ns
    const identifier = `quiz:${this.getAttribute('data-id')}`
    this.report = reports(namespace, identifier, this.getAttribute('data-reporting'))
    this.points = 0
    this.participants = 0
    this.reportLoaded = false

    const imageServiceConfig = this.getAttribute('data-image-service-config') ? JSON.parse(this.getAttribute('data-image-service-config')) : {}
    if (!imageServices[imageServiceConfig.name]) {
      console.error(`No ImageService available with the given name: ${imageServiceConfig.name}`)
      this.imageService = {
        getUrl(key) { return key }
      }
    } else {
      const ImageService = imageServices[imageServiceConfig.name];
      this.imageService = new ImageService(imageServiceConfig.config)
    }

    this.next()

    this.addEventListener('click', this._handleAction.bind(this))

    this.report.start()
    this.report.stats((err, body) => {
      if (err) return console.error(err)
      this.participants = body.uniqueFinishCount || 0
      this.reportLoaded = true

      // patch the element as we can't trigger a re-render because of css animations
      const text = translate(this.item, 'participants', {count: this.participants})
      const elem = this.querySelector('.js-quiz-header__item_participants')
      if (elem) elem.innerText = text
    })
  }

  _handleAction (evt) {
    const elem = evt.target.closest('[data-action]')
    const action = elem && elem.dataset.action

    if (action === 'vote') this.vote(elem)
    if (action === 'start') this.startAgain()
    if (action === 'next') this.next()
    if (action === 'summary') this.summary()
    if (action) evt.preventDefault()

    if (action) {
      try {
        const event = new CustomEvent("quiz-action", {
          bubbles: true,
          detail: {
            action: action
          }
        });
        this.dispatchEvent(event);
      } catch (e) {
        console.error("dispatching the quiz action event failed", e)
      }
    }
  }

  startAgain () {
    this.parentElement.appendChild(this.cloneNode(false))
    this.parentElement.removeChild(this)
  }

  render () {
    this.classList.add('swisscom-quiz-container')
    this.innerHTML = toHtml(this)
  }

  vote (elem) {
    this.selectedChoice = choiceById(this.question, elem.dataset.choiceId)
    this.correctChoice = choiceById(this.question, this.question.correctChoice) || {}
    this.hasChosenCorrectly = this.selectedChoice.id === this.correctChoice.id
    if (this.hasChosenCorrectly) {
      this.points++
      this.querySelector('.m-quiz-header__toprow__points').innerHTML = points(this)
    }

    // update the buttons
    this.querySelectorAll('[data-choice-id]').forEach(answerButton => {
      const answerChoiceId = answerButton.getAttribute('data-choice-id')
      if (answerChoiceId === this.correctChoice.id) {
        answerButton.classList.add('m-quiz-answer__button--correct')
      } else {
        answerButton.classList.add('m-quiz-answer__button--wrong')
      }
      if (answerChoiceId === this.selectedChoice.id) {
        answerButton.classList.add('m-quiz-answer__button--user-choice')
      }
      answerButton.setAttribute('disabled', true)
    })

    // show the results
    this.querySelector('.o-quiz-body__result').classList.remove('o-quiz-body__result--hidden')

    this.report.vote(this.question.id, this.selectedChoice.id)
  }

  next () {
    this.questionIndex = (this.questionIndex + 1) || 0
    this.question = this.item.questions[this.questionIndex]
    this.step = 'question'
    this.render()
  }

  summary () {
    this.step = 'summary'
    this.participants++

    // measure the header height to hardcode it once we switch to the summary view
    // because the summary view doesn't have content in the header
    this.fixedHeaderHeight = this.querySelector('.m-quiz-header').getBoundingClientRect().height;
    this.render()
    this.fixedHeaderHeight = undefined;

    this.report.finish()
  }
}

function questionHeader (state) {
  return simpleQuestionHeader(state, state.question.text)
}

function points(state) {
  return translate(state.item, 'points', {count: state.points || 0})
}

function simpleQuestionHeader (state, text) {
  if (state.step === 'question') {
    return `
      <div class="m-quiz-header" style="background-image: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #000000 100%), ${state.item.headerImage && state.item.headerImage.key ? `url('${state.imageService.getUrl(state.item.headerImage.key, {width: 1920})}')` : ''}">
        <div class="m-quiz-header__toprow">
          <div class="m-quiz-header__question-number">${state.questionIndex + 1}/${state.item.questions.length}</div>
          <div class="m-quiz-header__toprow__points">${points(state)}</div>
        </div>
        <h4 class="m-quiz-header__question">${text}</h4>
      </div>
    `
  } else {
    let style = '';
    if (state.fixedHeaderHeight) {
      style = `height: ${state.fixedHeaderHeight}px;`
    }
    return `
      <div class="m-quiz-header" style="${style} background-image: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #000000 100%), ${state.item.headerImage && state.item.headerImage.key ? `url('${state.imageService.getUrl(state.item.headerImage.key, {width: 1920})}')` : ''}">
      </div>
    `
  }
}

function choiceEntry (choice, i) {
  return `
    <div class="m-quiz-answer">
      <button class="a-button a-button--white a-button--font-large m-quiz-answer__button" data-choice-id="${choice.id}" data-action="vote">
        <span class="m-quiz-answer__button__text">${choice.text}</span>
      </button>
    </div>
  `
}

function quizHtml (state) {
  return `
    <div class="o-quiz">
      ${state.step === 'question' ? questionView(state) : ''}
      ${state.step === 'summary' ? summaryView(state) : ''}
    </div>
  `
}

function toHtml (state) {
  return `
    ${quizHtml(state)}
  `
}

function questionView (state) {

  const nextActionMarkup = state.questionIndex + 1 === state.item.questions.length
    ? `
      <button class="a-button a-button--white a-button--font-large " data-action="summary">${translate(state.item, 'toTheResults')}</button>
    ` : `
      <button class="a-button a-button--white a-button--font-large" data-action="next">${translate(state.item, 'nextQuestion')}</button>
    `

  return `
    ${questionHeader(state)}

    <div class="o-quiz__body">
      <div class="o-quiz-answers">
        ${state.question.choices.map(choiceEntry).join('\n')}
      </div>

      <div class="o-quiz-body__result o-quiz-body__result--hidden">
        <div class="o-quiz-body__result__feedback">
          ${state.question.feedbackText || ""}
        </div>
        <div class="o-quiz-body__result__action">
          ${nextActionMarkup}
        </div>
      </div>
    </div>
  `
}

function choiceById (question, id) {
  for (var i = 0; i < question.choices.length; i++) {
    if (question.choices[i].id === id) return question.choices[i]
  }
}

const robotSad = `<path d="M820.3 851.1h-614v-375c0-31.3 7.7-60 23-86s36-46.7 62-62 54.3-23 85-23h274c30.7 0 59 7.7 85 23s46.7 36 62 62c15.3 26 23 54.7 23 86v375zm103-409h-37c-6-38.7-20-73.5-42-104.5s-50-55.5-84-73.5-70.7-27-110-27h-55l79-78c4-4.7 6.8-10 8.5-16s1.8-12 .5-18-4.2-11.2-8.5-15.5-9.5-7.2-15.5-8.5-12-1.2-18 .5-11.3 4.5-16 8.5l-112 113-112-113c-4.7-4-10-6.8-16-8.5s-12-1.8-18-.5-11.2 4.2-15.5 8.5-7.2 9.5-8.5 15.5c-1.3 6-1.2 12 .5 18s4.5 11.3 8.5 16l79 78h-55c-39.3 0-76 9-110 27s-62 42.5-84 73.5-36 65.8-42 104.5h-37c-9.3 0-17.3 3.3-24 10-6.7 6.7-10 14.7-10 24s3.3 17.3 10 24c6.7 6.7 14.7 10 24 10h35v375c0 9.3 3.3 17.3 10 24 6.7 6.7 14.7 10 24 10h682c9.3 0 17.3-3.3 24-10 6.7-6.7 10-14.7 10-24v-375h35c6 0 11.7-1.5 17-4.5s9.5-7.2 12.5-12.5 4.5-11 4.5-17-1.5-11.7-4.5-17-7.2-9.5-12.5-12.5-11-4.5-17-4.5zm-258 242c-101.3-50.7-202.7-50.7-304 0-8 4-13.5 10.5-16.5 19.5s-2.5 17.8 1.5 26.5 10.5 14.5 19.5 17.5 17.8 2.2 26.5-2.5c80.7-40 161.3-40 242 0 8.7 4 17.5 4.7 26.5 2s15.5-8.3 19.5-17 4.5-17.5 1.5-26.5-8.5-15.5-16.5-19.5zm-15-174c-10 0-18.3-3.3-25-10s-10-14.7-10-24 3.3-17.3 10-24 14.8-10 24.5-10 17.8 3.3 24.5 10 10 14.7 10 24-3.3 17.3-10 24-14.7 10-24 10zm0-137c-28.7 0-53 10-73 30s-30 44.2-30 72.5 10 52.5 30 72.5 44.2 30 72.5 30 52.5-10 72.5-30 30-44.2 30-72.5-10-52.5-30-72.5-44-30-72-30zm-308 103c0-9.3 3.3-17.3 10-24s14.8-10 24.5-10 17.8 3.3 24.5 10 10 14.7 10 24-3.3 17.3-10 24-14.8 10-24.5 10-17.8-3.3-24.5-10-10-14.7-10-24zm137 0c0-28.7-10-53-30-73s-44.2-30-72.5-30-52.5 10-72.5 30-30 44.2-30 72.5 10 52.5 30 72.5 44.2 30 72.5 30 52.5-10 72.5-30 30-44 30-72z"/></svg>`
const robotNeutral = `<path d="M817.7 855.8h-614v-375c0-31.3 7.7-60 23-86s36-46.7 62-62 54.3-23 85-23h274c30.7 0 59 7.7 85 23s46.7 36 62 62c15.3 26 23 54.7 23 86v375zm103-409h-37c-6-38.7-20-73.5-42-104.5s-50-55.5-84-73.5-70.7-27-110-27h-55l79-78c4-4.7 6.8-10 8.5-16s1.8-12 .5-18-4.2-11.2-8.5-15.5-9.5-7.2-15.5-8.5-12-1.2-18 .5-11.3 4.5-16 8.5l-112 113-112-113c-4.7-4-10-6.8-16-8.5s-12-1.8-18-.5-11.2 4.2-15.5 8.5-7.2 9.5-8.5 15.5-1.2 12 .5 18 4.5 11.3 8.5 16l79 78h-55c-39.3 0-76 9-110 27s-62 42.5-84 73.5-36 65.8-42 104.5h-37c-9.3 0-17.3 3.3-24 10-6.7 6.7-10 14.7-10 24s3.3 17.3 10 24c6.7 6.7 14.7 10 24 10h35v375c0 9.3 3.3 17.3 10 24 6.7 6.7 14.7 10 24 10h682c9.3 0 17.3-3.3 24-10 6.7-6.7 10-14.7 10-24v-375h35c6 0 11.7-1.5 17-4.5s9.5-7.2 12.5-12.5 4.5-11 4.5-17-1.5-11.7-4.5-17-7.2-9.5-12.5-12.5-11-4.5-17-4.5zm-273 204h-274c-6 0-11.7 1.7-17 5-5.3 3.3-9.5 7.5-12.5 12.5s-4.5 10.7-4.5 17c0 6.3 1.5 12.2 4.5 17.5 3 5.3 7.2 9.5 12.5 12.5s11 4.5 17 4.5h274c6 0 11.7-1.5 17-4.5s9.5-7.2 12.5-12.5 4.5-11.2 4.5-17.5c0-6.3-1.5-12-4.5-17s-7.2-9.2-12.5-12.5c-5.4-3.3-11-5-17-5zm0-136c-10 0-18.3-3.3-25-10s-10-14.7-10-24 3.3-17.3 10-24 14.8-10 24.5-10 17.8 3.3 24.5 10 10 14.7 10 24-3.3 17.3-10 24-14.7 10-24 10zm0-137c-28.7 0-53 10-73 30s-30 44.2-30 72.5 10 52.5 30 72.5 44.2 30 72.5 30 52.5-10 72.5-30 30-44.2 30-72.5-10-52.5-30-72.5-44-30-72-30zm-308 103c0-9.3 3.3-17.3 10-24s14.8-10 24.5-10 17.8 3.3 24.5 10 10 14.7 10 24-3.3 17.3-10 24-14.8 10-24.5 10-17.8-3.3-24.5-10-10-14.7-10-24zm137 0c0-28.7-10-53-30-73s-44.2-30-72.5-30-52.5 10-72.5 30-30 44.2-30 72.5 10 52.5 30 72.5 44.2 30 72.5 30 52.5-10 72.5-30 30-44 30-72z"/>`
const robotHappy = `<path d="M820.3 851.4h-614v-375c0-31.3 7.7-60 23-86s36-46.7 62-62 54.3-23 85-23h274c30.7 0 59 7.7 85 23s46.7 36 62 62c15.3 26 23 54.7 23 86v375zm103-409h-37c-6-38.7-20-73.5-42-104.5s-50-55.5-84-73.5-70.7-27-110-27h-55l79-78c4-4.7 6.8-10 8.5-16s1.8-12 .5-18-4.2-11.2-8.5-15.5-9.5-7.2-15.5-8.5-12-1.2-18 .5-11.3 4.5-16 8.5l-112 113-112-113c-4.7-4-10-6.8-16-8.5s-12-1.8-18-.5-11.2 4.2-15.5 8.5-7.2 9.5-8.5 15.5c-1.3 6-1.2 12 .5 18s4.5 11.3 8.5 16l79 78h-55c-39.3 0-76 9-110 27s-62 42.5-84 73.5-36 65.8-42 104.5h-37c-9.3 0-17.3 3.3-24 10-6.7 6.7-10 14.7-10 24s3.3 17.3 10 24c6.7 6.7 14.7 10 24 10h35v375c0 9.3 3.3 17.3 10 24 6.7 6.7 14.7 10 24 10h682c9.3 0 17.3-3.3 24-10 6.7-6.7 10-14.7 10-24v-375h35c6 0 11.7-1.5 17-4.5s9.5-7.2 12.5-12.5 4.5-11 4.5-17-1.5-11.7-4.5-17-7.2-9.5-12.5-12.5-11-4.5-17-4.5zm-289 208c-80.7 40.7-161.3 40.7-242 0-8.7-4-17.5-4.5-26.5-1.5s-15.5 8.7-19.5 17-4.5 17-1.5 26 8.5 15.5 16.5 19.5c50.7 25.3 101.3 38 152 38s101.3-12.7 152-38c5.3-2.7 9.7-6.5 13-11.5s5.2-10.7 5.5-17c.3-6.3-.8-12.2-3.5-17.5-2.7-5.3-6.5-9.7-11.5-13-5-3.3-10.7-5.2-17-5.5-6.3-.3-12.1.9-17.5 3.5zm16-140c-10 0-18.3-3.3-25-10s-10-14.7-10-24 3.3-17.3 10-24 14.8-10 24.5-10 17.8 3.3 24.5 10 10 14.7 10 24-3.3 17.3-10 24-14.6 10-24 10zm0-137c-28.7 0-53 10-73 30s-30 44.2-30 72.5 10 52.5 30 72.5 44.2 30 72.5 30 52.5-10 72.5-30 30-44.2 30-72.5-10-52.5-30-72.5-44-30-72-30zm-308 103c0-9.3 3.3-17.3 10-24s14.8-10 24.5-10 17.8 3.3 24.5 10 10 14.7 10 24-3.3 17.3-10 24-14.8 10-24.5 10-17.8-3.3-24.5-10-10-14.6-10-24zm137 0c0-28.7-10-53-30-73s-44.2-30-72.5-30-52.5 10-72.5 30-30 44.2-30 72.5 10 52.5 30 72.5 44.2 30 72.5 30 52.5-10 72.5-30 30-44 30-72z"/>`

const ratingIconPaths = {
  0: robotSad,
  1: robotSad,
  2: robotNeutral,
  3: robotHappy,
  4: robotHappy
}

function summaryView (state) {
  const maxRating = 4
  const rating = getRatingForPoints(state.points, state.item.questions.length, maxRating)
  return `
    ${questionHeader(state)}
    <div class="o-quiz__body">
      <div class="o-quiz-summary">
        <div class="m-quiz-rating">
          <svg class="m-quiz-rating__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
            ${ratingIconPaths[rating]}
          </svg>
          <div class="m-quiz-rating__text">
            <span class="m-quiz-rating__points">
              ${translate(state.item, 'points', {count: state.points})}.
            </span>
            ${translate(state.item, 'ratingText', {count: rating})}
          </div>
        </div>
      </div>
      <div class="o-quiz__action">
        <button class="a-button a-button--white a-button--font-large" data-action="start">${translate(state.item, 'redoQuiz')}</button>
      </div>
    </div>
  `
}

module.exports = Tool


if (typeof window !== 'undefined' && !window.___BluewinQuizCustomElementRegistered) {
  class RegisteredTool extends Tool {}
  window.___BluewinQuizCustomElementRegistered = RegisteredTool
  window.customElements.define('bluewin-quiz', RegisteredTool)
}
